import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { PageHeader } from "../../components/dashboard/DashboardUI";
import {
  getAdminAllServicesTickets,
  getTicketTypes,
} from "../../redux/actions/TicketActions";
import { fetchAdminProcedures } from "../../redux/actions/ProcedureActions";
import { getTransactions } from "../../redux/actions/TransactionActions";
import { SERVICE_PAGES } from "../../utils/operatorPages";
import { formatPrice } from "../../utils/formatPrice";

const MONTHS = [
  "Janv.",
  "Févr.",
  "Mars",
  "Avr.",
  "Mai",
  "Juin",
  "Juil.",
  "Août",
  "Sept.",
  "Oct.",
  "Nov.",
  "Déc.",
];

const SERVICE_COLORS = {
  domiciliation: "#051259",
  admin: "#ff5500",
  juridique: "#2e7d32",
  management: "#0288d1",
  grh: "#8e24aa",
  sales: "#f9a825",
  odoo: "#6d4c41",
};

const emptyYear = (fields) =>
  MONTHS.map((month) => ({ month, ...fields }));

function Statistics() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { ticketTypes, adminAllServicesTickets } = useSelector(
    (state) => state.tickets,
  );
  const { procedures } = useSelector((state) => state.procedures);
  const { transactions } = useSelector((state) => state.transactions);

  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(moment().year());

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.all([
      dispatch(getTicketTypes()),
      dispatch(getAdminAllServicesTickets(user.token)),
      dispatch(fetchAdminProcedures(user.token)),
      dispatch(getTransactions(user.token)),
    ]).finally(() => setLoading(false));
  }, [dispatch, user?.token]);

  const inYear = (item) => moment(item.createdAt).year() === year;

  const years = useMemo(() => {
    const all = [
      ...(transactions || []),
      ...(adminAllServicesTickets || []),
      ...(procedures || []),
    ].map((item) => moment(item.createdAt).year());
    return [...new Set([moment().year(), ...all])]
      .filter(Boolean)
      .sort((a, b) => b - a);
  }, [transactions, adminAllServicesTickets, procedures]);

  // Service requests per month and per service
  const ticketData = useMemo(() => {
    const data = emptyYear(
      Object.fromEntries(SERVICE_PAGES.map((service) => [service.code, 0])),
    );
    (adminAllServicesTickets || []).filter(inYear).forEach((ticket) => {
      const service =
        (ticketTypes || [])
          .find((type) => type.code === ticket.reclamationCode)
          ?.service?.toLowerCase() || "odoo";
      const month = data[moment(ticket.createdAt).month()];
      if (month[service] !== undefined) month[service] += 1;
    });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAllServicesTickets, ticketTypes, year]);

  // Paid amounts and paid requests per month
  const revenueData = useMemo(() => {
    const data = emptyYear({ revenue: 0, requests: 0 });
    (transactions || [])
      .filter((t) => t.status === "completed" && inYear(t))
      .forEach((t) => {
        data[moment(t.createdAt).month()].revenue += Number(t.amount) || 0;
      });
    (procedures || [])
      .filter((r) => r.status !== "initiated" && inYear(r))
      .forEach((r) => {
        data[moment(r.createdAt).month()].requests += 1;
      });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, procedures, year]);

  const totalRevenue = revenueData.reduce((sum, m) => sum + m.revenue, 0);
  const totalRequests = revenueData.reduce((sum, m) => sum + m.requests, 0);
  const totalTickets = ticketData.reduce(
    (sum, m) =>
      sum + SERVICE_PAGES.reduce((acc, service) => acc + m[service.code], 0),
    0,
  );

  const stats = [
    { label: "Encaissé", value: formatPrice(totalRevenue) },
    { label: "Démarches payées", value: totalRequests },
    { label: "Demandes aux services", value: totalTickets },
  ];

  return (
    <>
      <PageHeader
        title="Statistiques"
        lead="Démarches, demandes aux services et paiements de l'année."
        actions={
          <select
            className="dash-select"
            value={year}
            onChange={(event) => setYear(Number(event.target.value))}
            aria-label="Année"
          >
            {years.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        }
      />

      <div className="dash-stats">
        {stats.map((stat) => (
          <div key={stat.label} className="dash-card dash-stat">
            <span>{stat.label}</span>
            <strong>{loading ? "…" : stat.value}</strong>
          </div>
        ))}
      </div>

      <section className="dash-card dash-chart">
        <h2 className="dash-card-title">Montants encaissés par mois</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tickLine={false} />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={80}
              tickFormatter={(value) =>
                new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(
                  value,
                )
              }
            />
            <Tooltip formatter={(value) => [formatPrice(value), "Encaissé"]} />
            <Bar dataKey="revenue" fill="#051259" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section className="dash-card dash-chart">
        <h2 className="dash-card-title">Demandes aux services par mois</h2>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={ticketData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tickLine={false} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
            <Tooltip />
            <Legend />
            {SERVICE_PAGES.map((service) => (
              <Line
                key={service.code}
                type="monotone"
                dataKey={service.code}
                name={service.label}
                stroke={SERVICE_COLORS[service.code]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </section>
    </>
  );
}

export default Statistics;
